'use strict';

import { applyFirstSpiritLazySizesConfig } from './firstspirit-lazyload-config';

import {
    initFirstSpiritCarousels,
    quarantineFirstSpiritCarousels,
} from './firstspirit-carousel';

/**
 * Initializes legacy FirstSpirit CMS markup embedded in Amplience surfaces.
 *
 * FirstSpirit content assets rely on lazysizes (`data-src`) and Born slick
 * carousels (`data-slick`). Preview/VSE loads amplience.js without app.js, so
 * this initializer runs in both preview and live storefront contexts.
 *
 * Lazysizes is bundled into amplience.js (not code-split) because SFCC static
 * delivery does not resolve webpack async chunk URLs for this bundle.
 */

/** @type {string} */
const FS_ROOT_SELECTOR = '.firstSpiritContentAsset';

/** @type {number} */
const IMAGE_READY_TIMEOUT_MS = 4000;

/** @type {number} */
const LAZYSIZES_WAIT_INTERVAL_MS = 50;

/** @type {number} */
const LAZYSIZES_WAIT_TIMEOUT_MS = 5000;

/**
 * Applies FirstSpirit lazysizes defaults to an existing lazysizes instance.
 *
 * @returns {void}
 * @private
 */
function _applyFirstSpiritLazySizesInstanceConfig() {
    if (!window.lazySizes || !window.lazySizes.cfg) {
        return;
    }

    window.lazySizes.cfg.ricTimeout = 100;
    window.lazySizes.cfg.loadHidden = false;
    window.lazySizes.cfg.loadMode = 1;
}

/**
 * Returns whether `app.js` is present in the document (live storefront footer).
 *
 * Must not run while `amplience.js` is still executing — the following `app.js`
 * tag is not parsed yet. Call from a deferred task after the current script ends.
 *
 * @returns {boolean}
 * @private
 */
function _documentHasAppJsScript() {
    let scripts = document.getElementsByTagName('script');

    for (let i = 0; i < scripts.length; i++) {
        let src = scripts[i].getAttribute('src') || '';

        if (src.indexOf('app.js') !== -1) {
            return true;
        }
    }

    return false;
}

/**
 * Initializes the sync-bundled Amplience lazysizes copy for preview-only pages.
 *
 * @returns {void}
 * @private
 */
function _initAmplienceLazySizes() {
    require('./firstspirit-lazysizes-init');
    _applyFirstSpiritLazySizesInstanceConfig();

    if ('lazySizesConfig' in window) {
        delete window.lazySizesConfig;
    }
}

/**
 * Waits for Born's async lazysizes init on live storefront pages.
 *
 * @returns {Promise<void>}
 * @private
 */
function _whenBornLazySizesReady() {
    return new Promise((resolve) => {
        let elapsed = 0;

        /**
         * @returns {void}
         */
        function check() {
            if (window.lazySizes) {
                _applyFirstSpiritLazySizesInstanceConfig();

                if ('lazySizesConfig' in window) {
                    delete window.lazySizesConfig;
                }

                resolve();
                return;
            }

            elapsed += LAZYSIZES_WAIT_INTERVAL_MS;

            if (elapsed >= LAZYSIZES_WAIT_TIMEOUT_MS) {
                _initAmplienceLazySizes();
                resolve();
                return;
            }

            window.setTimeout(check, LAZYSIZES_WAIT_INTERVAL_MS);
        }

        check();
    });
}

/**
 * Ensures lazysizes is configured and initialized for FirstSpirit content.
 *
 * VSE/preview loads only amplience.js and uses the sync-bundled lazysizes copy.
 * Live storefront defers to Born's async lazysizes so amplience does not init a
 * second instance that `app.js` later replaces. `app.js` cannot be detected while
 * `amplience.js` is still running, so the branch is chosen on the next task.
 *
 * @returns {Promise<void>}
 * @private
 */
function _ensureLazySizes() {
    if (window.lazySizes) {
        _applyFirstSpiritLazySizesInstanceConfig();
        return Promise.resolve();
    }

    return new Promise((resolve) => {
        window.setTimeout(() => {
            if (_documentHasAppJsScript()) {
                applyFirstSpiritLazySizesConfig();
                _whenBornLazySizesReady().then(resolve);
                return;
            }

            _initAmplienceLazySizes();
            resolve();
        }, 0);
    });
}

/**
 * Forces lazysizes to evaluate images inside a FirstSpirit content root.
 *
 * @param {HTMLElement} root - FirstSpirit content asset container.
 * @returns {void}
 * @private
 */
function _refreshLazyImages(root) {
    if (!window.lazySizes || !window.lazySizes.loader) {
        return;
    }

    let lazyImages = root.querySelectorAll('.lazyload');

    for (let i = 0; i < lazyImages.length; i++) {
        window.lazySizes.loader.unveil(lazyImages[i]);
    }

    window.lazySizes.loader.checkElems();
}

/**
 * Reveals `data-motion` elements embedded in FirstSpirit content when page
 * motion styles would otherwise keep them at opacity 0 without app.js.
 *
 * @param {HTMLElement} root - FirstSpirit content asset container.
 * @returns {void}
 * @private
 */
function _revealDataMotion(root) {
    if (!root.closest('[data-page-motion]')) {
        return;
    }

    let motionEls = root.querySelectorAll('[data-motion]');

    for (let i = 0; i < motionEls.length; i++) {
        let motionEl = motionEls[i];
        let props = (motionEl.getAttribute('data-motion') || '')
            .trim()
            .split(/\s+/);

        for (let j = 0; j < props.length; j++) {
            if (props[j]) {
                motionEl.classList.add(`set--motion-${props[j]}`);
            }
        }

        motionEl.classList.add('set--motion-trigger');
    }
}

/**
 * Returns whether an image has finished loading (or failed) for layout sizing.
 *
 * Lazysizes images with a placeholder `src` are not ready until `lazyloaded`.
 *
 * @param {HTMLImageElement} image - Image to inspect.
 * @returns {boolean}
 * @private
 */
function _isImageReady(image) {
    if (image.classList.contains('lazyloaded')) {
        return true;
    }

    if (image.getAttribute('data-src') || image.getAttribute('data-srcset')) {
        return false;
    }

    if (image.complete) {
        return true;
    }

    return false;
}

/**
 * Waits until images inside a FirstSpirit root are ready to size layout.
 *
 * @param {HTMLElement} root - FirstSpirit content asset container.
 * @returns {Promise<void>}
 * @private
 */
function _whenImagesReady(root) {
    let images = root.querySelectorAll('img');

    if (!images.length) {
        return Promise.resolve();
    }

    return new Promise((resolve) => {
        let resolved = false;
        let listeners = [];

        /**
         * @returns {void}
         */
        function finish() {
            if (!resolved) {
                resolved = true;

                for (let i = 0; i < listeners.length; i++) {
                    let listener = listeners[i];

                    listener.image.removeEventListener(
                        listener.type,
                        listener.handler,
                    );
                }

                resolve();
            }
        }

        /**
         * @returns {boolean}
         */
        function allImagesReady() {
            for (let i = 0; i < images.length; i++) {
                if (!_isImageReady(images[i])) {
                    return false;
                }
            }

            return true;
        }

        /**
         * @returns {void}
         */
        function checkAndFinish() {
            if (allImagesReady()) {
                finish();
            }
        }

        if (allImagesReady()) {
            finish();
            return;
        }

        for (let i = 0; i < images.length; i++) {
            let image = images[i];
            let eventTypes = ['load', 'error', 'lazyloaded'];

            for (let j = 0; j < eventTypes.length; j++) {
                listeners.push({
                    image: image,
                    type: eventTypes[j],
                    handler: checkAndFinish,
                });
                image.addEventListener(eventTypes[j], checkAndFinish);
            }
        }

        window.setTimeout(finish, IMAGE_READY_TIMEOUT_MS);
    });
}

/**
 * Recalculates slick layout after lazy images finish loading.
 *
 * @param {JQuery} $carousel - jQuery-wrapped slick root element.
 * @returns {void}
 * @private
 */
function _refreshCarouselLayout($carousel) {
    if (!$carousel.hasClass('slick-initialized')) {
        return;
    }

    if ($carousel.data('fsRefreshingLayout')) {
        return;
    }

    $carousel.data('fsRefreshingLayout', true);

    try {
        $carousel.slick('setPosition');
    } finally {
        $carousel.data('fsRefreshingLayout', false);
    }
}

/**
 * Binds carousel events so lazysizes and slick stay in sync.
 *
 * @param {JQuery} $carousel - jQuery-wrapped slick root element.
 * @param {HTMLElement} root - FirstSpirit content asset container.
 * @returns {void}
 * @private
 */
function _bindCarouselEvents($carousel, root) {
    $carousel.on('afterChange reInit', () => {
        _refreshLazyImages(root);
        _refreshCarouselLayout($carousel);
    });

    root.addEventListener(
        'lazyloaded',
        (event) => {
            if (!(event.target instanceof HTMLImageElement)) {
                return;
            }

            if (!root.contains(event.target)) {
                return;
            }

            if (!$carousel[0].contains(event.target)) {
                return;
            }

            _refreshCarouselLayout($carousel);
        },
        true,
    );
}

/**
 * Initializes lazysizes and slick carousels for one FirstSpirit content root.
 *
 * @param {HTMLElement} root - FirstSpirit content asset container.
 * @returns {void}
 * @private
 */
function _initFirstSpiritRoot(root) {
    _refreshLazyImages(root);

    _whenImagesReady(root).then(() => {
        let carousels = initFirstSpiritCarousels(root);

        for (let i = 0; i < carousels.length; i++) {
            _bindCarouselEvents(carousels[i], root);

            window.requestAnimationFrame(() => {
                _refreshCarouselLayout(carousels[i]);
            });
        }
    });
}

/**
 * Initializes all FirstSpirit content assets on the current page.
 *
 * @returns {void}
 */
export function initFirstSpiritContent() {
    let roots = document.querySelectorAll(FS_ROOT_SELECTOR);

    if (!roots.length) {
        return;
    }

    for (let i = 0; i < roots.length; i++) {
        _revealDataMotion(roots[i]);
        quarantineFirstSpiritCarousels(roots[i]);
    }

    _ensureLazySizes().then(() => {
        for (let i = 0; i < roots.length; i++) {
            _initFirstSpiritRoot(roots[i]);
        }
    });
}
